import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import Page from "../../layout/Page";
import { Helmet } from "react-helmet";
import DocumentTitle from "../../functional-components/DocumentTitle";
import Herobox from "../../components/Herobox";
import ProductsPageAside from "../../components/ProductsPageAside";
import {
  decodeFilterQstring,
  encodeFilterQstring,
  filterProducts,
  SORT_BY,
  sortProducts
} from "../../utils/ProductsUtils";
import ProductGrid from "../../components/ProductGrid";
import OptionButton from "../../components/OptionButton";
import { useMediaQuery } from "react-responsive";
import useWindowSize from "../../hooks/useWindowSize";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import { cmsAssetUrl } from "../../utils/CmsUtils";
import PageNotFoundFragment from "../../fragments/PageNotFoundFragment";
import { useCurrentUser } from "../../functional-components/UserContext";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import Icon from "../../components/Icon";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import BjelinLogo from "../../components/BjelinLogo";
import { useRootCategories, useUserProducts } from "../../hooks/ProductHooks";
import SimpleLoadingSpinner from "../../components/SimpleLoadingSpinner";
import { shopUrl } from "../../config/urls";
import NextSlideButton from "../../components/SectionNavigation/NextSlideButton";
import useScrollThreshold from "../../hooks/useScrollThreshold";
import {
  CategoryNav,
  CategorySubTreeNav
} from "../../components/CategoryLinks";
import "./style.scss";
import {
  categoryIsListable,
  enabledOnSites,
  filterProductsByCategory,
  getProductCategoryPedigree,
  productCategoryTitle,
  productCategoryUrl,
  productCategoryUrls,
  useCategoryFromPath
} from "../../utils/ProductCategoryUtils";
import useResizeObserver from "@react-hook/resize-observer";
import { Input } from "reactstrap";
import useShowPricesWithVat from "../../hooks/useShowPricesWithVat";

const ProductsPageContent = ({ category, categoryNavIsNeeded }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const settings = useLocSettings();
  const features = useSiteFeatures();
  const showPricesWithVat = useShowPricesWithVat();
  const { admin, b2b } = useCurrentUser();
  const isSmall = useMediaQuery({ maxWidth: 575.98 });
  const isLarge = useMediaQuery({ minWidth: 1124 });
  const winDim = useWindowSize();
  const products = useUserProducts();
  const isScrolled = useScrollThreshold(10);
  const prevCategoryKey = useRef(category?.key);

  const [filterExpanded, setFilterExpanded] = useState(false);
  const [sort, setSort] = useState(
    searchParams.get("sort") ?? SORT_BY.relevance
  );
  const [layout, setLayout] = useState(
    searchParams.has("layout") && !isSmall
      ? searchParams.get("layout")
      : b2b
      ? "list"
      : "card"
  );
  const [activeFilters, setActiveFilters] = useState(
    decodeFilterQstring(searchParams.get("filter"))
  );
  const filterCol = useRef();
  const container = useRef();
  const [filterHeight, setFilterHeight] = useState(500);

  const filterFields = useMemo(
    () => category?.available_filters || [],
    [category?.available_filters]
  );
  const [onlySaleable, setOnlySaleable] = useState(false);

  const topBarHeight = 4.25; //rem
  const filterWidth = 18; //rem
  const filterColMargin = 0.25; //rem
  const secondaryTopBarHeight = categoryNavIsNeeded ? 4 : 0;
  const totalTopBarHeight = topBarHeight + secondaryTopBarHeight;

  const colorFilterName = "Staining";
  const gradeFilterName = "Grading";

  const [filterStyle, setFilterStyle] = useState({
    top:
      "calc(var(--padding-top) + " + (topBarHeight + filterColMargin) + "rem)"
  });

  //Fix sorting on category switch
  useEffect(() => {
    if (category?.key !== prevCategoryKey.current) {
      if (sort === SORT_BY.grade && !filterFields.includes(gradeFilterName)) {
        setSort(SORT_BY.relevance);
      } else if (
        sort === SORT_BY.color &&
        !filterFields.includes(colorFilterName)
      ) {
        setSort(SORT_BY.relevance);
      }
      prevCategoryKey.current = category?.key;
    }
  }, [category?.key, filterFields, sort]);

  useEffect(() => {
    const sort = searchParams.get("sort");
    const layout = searchParams.get("layout");
    if (sort) setSort(parseInt(sort));
    if (layout && !isSmall) setLayout(layout);
    setActiveFilters(decodeFilterQstring(searchParams.get("filter")));
  }, [isSmall, searchParams]);

  const setFilter = (field, value, set) => {
    const fs = { ...activeFilters };
    if (typeof fs[field] === "undefined") fs[field] = [];
    if (set) {
      if (!fs[field].includes(value)) fs[field].push(value);
    } else {
      fs[field] = fs[field].filter(v => v !== value);
      if (fs[field].length === 0) delete fs[field];
    }
    setActiveFilters(fs);
    setSearchParams(
      searchParams => {
        searchParams.set("filter", encodeFilterQstring(fs));
        return searchParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setActiveFilters({});
    setSearchParams(
      searchParams => {
        searchParams.set("filter", "");
        return searchParams;
      },
      { replace: true }
    );
  };

  const changeSorting = value => {
    setSort(parseInt(value));
    setSearchParams(
      searchParams => {
        searchParams.set("sort", value);
        return searchParams;
      },
      { replace: true }
    );
  };

  const changeLayout = value => {
    setLayout(value);
    setSearchParams(
      searchParams => {
        searchParams.set("layout", value);
        return searchParams;
      },
      { replace: true }
    );
  };

  const valueOrder = settings.specification_value_order;

  const baseFontSize = parseInt(
    window
      .getComputedStyle(document.documentElement)
      .getPropertyValue("font-size")
      .match(/\d+/)?.[0] || "16"
  );

  const isFiltered = Object.keys(activeFilters).length > 0;

  const productsInCategory = useMemo(
    () => filterProductsByCategory(products, category, true),
    [products, category]
  );
  const totalProducts = useMemo(
    () => productsInCategory.length,
    [productsInCategory]
  );
  const filteredProducts = useMemo(
    () =>
      filterProducts(productsInCategory, activeFilters || {}).filter(
        product => !onlySaleable || product.saleable || product.sampleSaleable
      ),
    [productsInCategory, activeFilters, onlySaleable]
  );

  const offset = useRef(0);
  const scrollingUp = useRef(true);
  const lastScroll = useRef(window.scrollY);
  const handleScroll = useCallback(() => {
    if (container.current.classList.contains("small")) {
      return;
    }
    //scrolling down
    if (window.scrollY > lastScroll.current) {
      if (scrollingUp.current) {
        offset.current = filterCol.current.offsetTop;
        scrollingUp.current = false;
      }
      let top =
        (window.innerHeight -
          filterCol.current.getBoundingClientRect().height) /
        baseFontSize;
      top = top > totalTopBarHeight ? totalTopBarHeight : top;
      setFilterStyle({
        top: top + "rem",
        marginTop: offset.current / baseFontSize + "rem",
        alignSelf: null,
        marginBottom: null,
        bottom: null
      });
    } else {
      //scrolling up
      if (!scrollingUp.current) {
        offset.current =
          container.current.getBoundingClientRect().height -
          filterCol.current.offsetTop -
          filterCol.current.getBoundingClientRect().height;
        scrollingUp.current = true;
      }
      setFilterStyle({
        marginBottom: offset.current / baseFontSize + "rem",
        marginTop: null,
        alignSelf: "end",
        bottom:
          (-filterCol.current.getBoundingClientRect().height +
            window.innerHeight) /
            baseFontSize -
          totalTopBarHeight +
          "rem",
        top: null
      });
    }
    lastScroll.current = window.pageYOffset;
  }, [baseFontSize, totalTopBarHeight]);

  useLayoutEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleResize = useCallback(() => {
    if (container.current.classList.contains("small")) {
      return;
    }
    if (scrollingUp.current) {
      scrollingUp.current = false;
      offset.current = filterCol.current.offsetTop;
      let top =
        (window.innerHeight -
          filterCol.current.getBoundingClientRect().height) /
        baseFontSize;
      top = top > totalTopBarHeight ? totalTopBarHeight : top;

      setFilterStyle({
        top: top + "rem",
        marginTop: offset.current + "px",
        alignSelf: null,
        marginBottom: null,
        bottom: null
      });
    }
  }, [baseFontSize, totalTopBarHeight]);

  useResizeObserver(filterCol, () => {
    handleResize();
  });
  const windowSize = useWindowSize();

  useEffect(() => {
    handleResize();
  }, [totalProducts, handleResize, windowSize]);

  useLayoutEffect(() => {
    setFilterHeight(filterCol.current ? filterCol.current.offsetHeight : 500);
  }, [totalProducts]);

  return (
    <div
      style={{
        position: "relative",
        minHeight: filterHeight
      }}
      className={["products-grid", isLarge ? "large" : "small"].join(" ")}
      ref={container}
    >
      <motion.div
        id="filterCol"
        ref={filterCol}
        style={
          !isLarge
            ? {
                top: topBarHeight + filterColMargin + "rem",
                ...(!isLarge
                  ? {
                      x: -filterWidth + filterColMargin + "rem"
                    }
                  : {}),
                width: filterWidth - 2 * filterColMargin + "rem"
              }
            : {
                ...filterStyle,
                width: filterWidth - 2 * filterColMargin + "rem"
              }
        }
        animate={{
          ...(!isLarge
            ? {
                x: filterExpanded
                  ? 0
                  : -(filterWidth + 2 * filterColMargin) + "rem"
              }
            : {}),
          width: filterWidth - 2 * filterColMargin + "rem"
        }}
      >
        <ProductsPageAside
          category={category}
          products={filteredProducts}
          allProducts={productsInCategory}
          activeFilters={activeFilters}
          setFilter={setFilter}
          resetFilters={resetFilters}
        />
      </motion.div>
      {!isLarge && (
        <motion.div
          id="filterTab"
          style={
            {
              // top: `calc(var(--padding-top) + ${topBarHeight}rem`
            }
          }
          animate={{
            x: filterExpanded ? filterWidth - 4.75 + "rem" : 0
          }}
          transition={{
            duration: 0.5,
            type: "spring"
          }}
          onClick={() => setFilterExpanded(!filterExpanded)}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 4rem",
              lineHeight: "4rem",
              fontSize: "2.25rem",
              padding: ".25rem 0"
            }}
          >
            <Icon name={filterExpanded ? "xmark" : "sliders"} type="fal" />
          </div>
          <motion.div
            style={{
              height: "4rem",
              overflow: "hidden"
            }}
            animate={{
              width: isScrolled || filterExpanded ? 0 : "auto",
              opacity: isScrolled || filterExpanded ? 0 : 1
            }}
            transition={{
              duration: 0.5,
              type: "spring"
            }}
          >
            <div
              style={{
                height: "4rem",
                padding: "0 1.5rem 0 0",
                lineHeight: "4rem",
                fontSize: "0.6em"
              }}
            >
              {t("action.filter")}
            </div>
          </motion.div>
        </motion.div>
      )}
      <section
        id="productsCol"
        aria-label={tp("products")}
        style={{
          ...(!isLarge
            ? {
                transform: `scale(${
                  filterExpanded
                    ? (winDim.innerWidth -
                        filterWidth * baseFontSize -
                        (baseFontSize - 1)) /
                      winDim.innerWidth
                    : 1
                }) translateX(${
                  filterExpanded
                    ? (-8 * winDim.innerWidth) /
                      Math.max(
                        winDim.innerWidth - filterWidth * baseFontSize,
                        1
                      )
                    : 0
                }px)`,
                marginTop: 0
              }
            : {}),
          padding: isSmall ? "0 1rem" : "0 1.875rem"
        }}
      >
        <>
          <motion.div
            id="productsHeader"
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: 4,
              height: isLarge ? "5rem" : "4rem",
              marginTop: "0.625rem",
              marginBottom: "1.25rem"
            }}
          >
            <div
              style={{
                flex: isLarge ? "0 0 2.625rem" : "0 0 1.875rem",
                display: "flex",
                alignItems: "center",
                columnGap: "1em"
              }}
            >
              {isFiltered ? (
                <span>
                  {t("product-grid.matches", {
                    count: filteredProducts.length,
                    total: totalProducts
                  })}
                </span>
              ) : (
                <span>
                  {tp("showing", {
                    entries: filteredProducts.length
                  })}
                </span>
              )}
              <span>
                {!features.shop() &&
                  (showPricesWithVat
                    ? tp("retail_prices_incl_vat")
                    : tp("retail_prices_excl_vat"))}
              </span>
            </div>
            <div
              style={{
                flex: "0 0 1.875rem",
                display: "flex",
                alignItems: "center"
              }}
            >
              <strong className={"me-1"}>{t("sort.by.label")}:</strong>
              <select
                id={"sort-select"}
                onChange={e => changeSorting(e.currentTarget.value)}
                value={sort}
                aria-label={t("sort.by.label")}
              >
                <option value={SORT_BY.relevance}>
                  {t("sort.by.relevance")}
                </option>
                {features.prices() && (
                  <>
                    <option value={SORT_BY.price_asc}>
                      {t("sort.by.price_asc")}
                    </option>
                    <option value={SORT_BY.price_desc}>
                      {t("sort.by.price_desc")}
                    </option>
                  </>
                )}
                <option value={SORT_BY.name}>{t("sort.by.name")}</option>
                {filterFields.includes(gradeFilterName) && (
                  <option value={SORT_BY.grade}>{t("sort.by.grade")}</option>
                )}
                {filterFields.includes(colorFilterName) && (
                  <option value={SORT_BY.color}>{t("sort.by.color")}</option>
                )}
              </select>
              {!isSmall && (
                <div className={"me-1"}>
                  <strong className={"ms-3 me-1"}>
                    {t("display.as.label")}:
                  </strong>
                  <OptionButton
                    options={{
                      card: t("display.as.card"),
                      list: t("display.as.list")
                    }}
                    value={layout}
                    onChange={value => changeLayout(value)}
                  />
                </div>
              )}
              {admin && !isSmall && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <strong className={"me-1"}>
                    {t("products.filter.saleable.label")}:
                  </strong>
                  <Input
                    id={"onlySaleable-input"}
                    aria-label={t("products.filter.saleable.label")}
                    type={"checkbox"}
                    checked={onlySaleable}
                    onChange={e => setOnlySaleable(e.target.checked)}
                  />
                </div>
              )}{" "}
            </div>
          </motion.div>

          <AnimatePresence initial={false}>
            {filteredProducts.length ? (
              <ProductGrid
                key={category?.key}
                products={sortProducts(
                  filteredProducts,
                  parseInt(sort),
                  valueOrder
                )}
                preferredCategory={category}
                groupBy={parseInt(sort)}
                layout={layout}
                clickToFilter
              />
            ) : (
              <motion.div
                role="alert"
                className="empty-msg"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1
                  }
                }}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <div>
                  <Icon name="face-sad-tear" size="4x" />
                </div>
                <div>{t("product-grid.no-match")}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      </section>
      {!isLarge && (
        <AnimatePresence>
          {filterExpanded && (
            <motion.div
              id="productGridCover"
              style={{
                position: "fixed",
                zIndex: 2,
                top: isLarge ? 0 : "var(--padding-top)",
                bottom: 0,
                left: 0,
                right: 0,
                background: "rgba(0,0,0,0.5)"
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFilterExpanded(false)}
            />
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

const ProductsPage = () => {
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const features = useSiteFeatures();
  const catTree = useRootCategories();
  const products = useUserProducts();

  const heroBox = useRef();
  const wrapperRef = useRef();
  const [heroBoxHeight, setHeroBoxHeight] = useState(200);

  useEffect(() => {
    setHeroBoxHeight(heroBox.current?.offsetHeight || 200);
  }, []);

  const hideScrollButton = useScrollThreshold(heroBoxHeight / 3);
  const isLarge = useMediaQuery({ minWidth: 1124 });
  const isSmall = useMediaQuery({ maxWidth: 576 });

  const { category: catPath } = useParams();

  //can be undefined
  const _category = useCategoryFromPath(catPath);
  //never undefined
  const category = useMemo(() => _category || {}, [_category]);
  const categoryPath = useMemo(
    () => productCategoryUrl(_category),
    [_category]
  );
  const productCategoryPedigree = getProductCategoryPedigree(category);
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  useEffect(() => {
    registerCurrentPaths(productCategoryUrls(_category));
    setAvailableOnSites(enabledOnSites(_category));
  }, [registerCurrentPaths, setAvailableOnSites, _category]);

  const title = productCategoryTitle(category, tp);

  if (categoryPath && `${shopUrl}/${catPath}` !== categoryPath) {
    return <Navigate to={categoryPath} replace />;
  }

  const heroBoxBkgPath =
    category.shop_herobox_background_image &&
    category.shop_herobox_background_image.path;
  const description = t("category.meta-description", { category: title });

  let categoryLogo;
  let heroBoxShadow;
  if (catPath === "outlet") {
    categoryLogo = <BjelinLogo variant={"outlet"} />;
    heroBoxShadow = false;
  } else if (catPath === "concept") {
    categoryLogo = <BjelinLogo variant={"concept"} darkBG={true} />;
    heroBoxShadow = true;
  } else {
    categoryLogo = title;
    heroBoxShadow = true;
  }

  const breadcrumbItems = [
    features.shop()
      ? { url: shopUrl, text: t("shop") }
      : { url: shopUrl, text: t("menu-label.products") }
  ];
  productCategoryPedigree.forEach(cat => {
    breadcrumbItems.push({
      url: productCategoryUrl(cat),
      text: productCategoryTitle(cat, tp)
    });
  });

  const heroboxContent =
    category.page_content?.[0]?.component === "content_section"
      ? category.page_content?.[0]?.children
      : category.page_content;

  const categoryNavIsNeeded =
    catTree.filter(category => categoryIsListable(category, products)).length >
    1;

  return !_category ? (
    <Page title={""} name={"404"} key={"products-page-404"} transition="simple">
      <PageNotFoundFragment />
    </Page>
  ) : (
    <>
      <DocumentTitle title={title + " - " + t("shop")} />
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"website"} />
        <meta property="og:title" content={title + " - " + t("shop")} />
        <meta
          property="og:description"
          name="description"
          content={description}
        />
        <meta property="og:image" content={heroBoxBkgPath} />
        {/*<meta property="fb:app_id" content={} />*/}
      </Helmet>

      <Page
        name="products-page"
        key="products-page"
        title={title + " - " + t("shop")}
        scrollToTop="no"
        noindex={!categoryIsListable(category, products)}
      >
        {isLarge && categoryNavIsNeeded && (
          <CategoryNav curCat={category} categories={catTree} />
        )}
        <Herobox
          ref={heroBox}
          textShadow={heroBoxShadow}
          className={"text-center"}
          backgroundImage={
            heroBoxBkgPath != null ? cmsAssetUrl(heroBoxBkgPath) : null
          }
          ariaLabel={t("aria.label.header")}
        >
          <h1 style={{ marginTop: "1em" }}>{categoryLogo}</h1>
          {heroboxContent && (
            <CockpitLayoutComponents
              components={heroboxContent}
              style={{ marginBottom: "2rem" }}
            />
          )}
          {category.children.length > 0 && (
            <CategorySubTreeNav category={category} />
          )}
          {category.parent && (
            <div
              className="category-back-button"
              style={{
                top: categoryNavIsNeeded
                  ? isLarge
                    ? "calc(var(--menu-height) + 3rem)"
                    : "calc(var(--menu-height) + 4.5rem)"
                  : "calc(var(--menu-height) + 1rem)"
              }}
            >
              <Link
                to={productCategoryUrl(category.parent)}
                style={{
                  lineHeight: "3em",
                  fontWeight: 600,
                  fontSize: "1.2em"
                }}
              >
                <Icon name="arrow-left" />{" "}
                {productCategoryTitle(category.parent, tp)}
              </Link>
            </div>
          )}
          <NextSlideButton
            onClick={() => {
              window.scrollTo({
                top: heroBoxHeight - 128,
                behavior: "smooth"
              });
            }}
            slideDirection={"vertical"}
            direction={1}
            hidden={hideScrollButton}
            style={{ position: "absolute", bottom: isSmall ? "2rem" : "1rem" }}
          />
        </Herobox>

        <div className="content-wrapper" ref={wrapperRef}>
          <Suspense
            fallback={
              <div
                className="center-content"
                style={{ height: "100dvh", paddingBottom: "25vh" }}
                aria-busy
              >
                <SimpleLoadingSpinner />
                <div>{tp("loading_products")}</div>
              </div>
            }
          >
            <ProductsPageContent
              category={_category}
              categoryNavIsNeeded={categoryNavIsNeeded}
            />
          </Suspense>
        </div>
      </Page>
    </>
  );
};

export default ProductsPage;
